import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { IdeaListItem, IdeaStatus, Paginated } from '@shared/types';
import { IDEA_STATUSES } from '@shared/types';
import { IDEA_STATUS_DESCRIPTIONS, IDEA_STATUS_LABELS } from '@shared/ideaStatus';
import { tagTone } from '@shared/format';
import { useAuthStore } from '../../app/authStore';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, EmptyState, Spinner } from '../../components/Card';
import { TextInput } from '../../components/Field';
import { api, errorMessage } from '../../lib/api';
import { useIdeasStore } from './ideasStore';
import { useSettingsStore } from '../admin/settingsStore';
import { IdeaCard } from './components/IdeaCard';

/** 看板列：主流程的五步。归档不在列里，它是一条旁路。 */
const COLUMNS: readonly IdeaStatus[] = ['submitted', 'validating', 'validated', 'incubating', 'shipped'];

export function BoardPage() {
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const items = useIdeasStore((state) => state.items);
  const loading = useIdeasStore((state) => state.loading);
  const loaded = useIdeasStore((state) => state.loaded);
  const error = useIdeasStore((state) => state.error);
  const filters = useIdeasStore((state) => state.filters);
  const load = useIdeasStore((state) => state.load);
  const setFilters = useIdeasStore((state) => state.setFilters);
  const settings = useSettingsStore((state) => state.settings);
  const loadSettings = useSettingsStore((state) => state.load);

  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [drafts, setDrafts] = useState<IdeaListItem[]>([]);
  const [archived, setArchived] = useState<IdeaListItem[]>([]);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  // 看板只关心「进行中」的状态；归档由下面的独立请求负责，
  // 这样切到归档视图不会把看板的数据集换掉。
  useEffect(() => {
    void load();
  }, [load]);

  // 草稿是私事：单独取一次，且只有自己能看到
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void api
      .get<Paginated<IdeaListItem>>('/api/ideas', { query: { authorId: user.id, includeDrafts: 1, status: 'draft', pageSize: 50 } })
      .then((result) => {
        if (!cancelled) setDrafts(result.items);
      })
      .catch(() => {
        if (!cancelled) setDrafts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // 归档单独拉一次：默认看板数据集里不含归档，混在一起会让两个视图互相污染
  useEffect(() => {
    if (!showArchived) return;
    let cancelled = false;
    void api
      .get<Paginated<IdeaListItem>>('/api/ideas', { query: { status: 'archived', pageSize: 200 } })
      .then((result) => {
        if (!cancelled) setArchived(result.items);
      })
      .catch((fetchError) => {
        if (!cancelled) setArchived([]);
        setArchiveError(errorMessage(fetchError));
      });
    return () => {
      cancelled = true;
    };
  }, [showArchived]);

  const grouped = useMemo(() => {
    const map = new Map<IdeaStatus, IdeaListItem[]>();
    for (const status of COLUMNS) map.set(status, []);
    for (const item of items) {
      const bucket = map.get(item.status);
      if (bucket) bucket.push(item);
    }
    return map;
  }, [items]);

  const tagOptions = settings.tags;
  const canCreate = permissions.includes('idea.create');

  return (
    <PageContainer>
      <PageHeader
        title="创意看板"
        description="从待验证到已上线。任何成员都可以提创意，AI 在几分钟内给出可行性研判与产物草稿。"
        actions={
          canCreate ? (
            <Link to="/ideas/new">
              <Button variant="primary">提出创意</Button>
            </Link>
          ) : null
        }
      />

      <Card>
        <CardBody className="flex flex-wrap items-center gap-2">
          <div className="w-56">
            <TextInput
              placeholder="搜索标题或正文"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') setFilters({ search: search.trim() });
              }}
            />
          </div>
          <Button size="sm" variant="default" onClick={() => setFilters({ search: search.trim() })}>
            搜索
          </Button>

          <div className="flex flex-wrap items-center gap-1">
            {tagOptions.map((tag) => {
              const active = filters.tags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setFilters({ tags: active ? filters.tags.filter((item) => item !== tag) : [...filters.tags, tag] })}
                >
                  <Badge tone={active ? tagTone(tag) : 'neutral'} solid={active}>
                    {tag}
                  </Badge>
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant={showArchived ? 'default' : 'ghost'} onClick={() => setShowArchived((value) => !value)}>
              {showArchived ? '返回看板' : '查看归档'}
            </Button>
            <Button size="sm" variant="ghost" loading={loading} onClick={() => void load({ force: true })}>
              刷新
            </Button>
          </div>
        </CardBody>
      </Card>

      {error ? <p className="text-xs text-critical">{error}</p> : null}

      {!loaded && loading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Spinner /> 正在加载创意…
        </div>
      ) : null}

      {showArchived ? (
        <Card>
          <CardBody>
            {archiveError ? <p className="text-xs text-critical">{archiveError}</p> : null}
            {archived.length === 0 ? (
              <EmptyState title="没有归档的创意" description="归档后会从看板移出，讨论与研判结论仍然保留。" />
            ) : (
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {archived.map((item) => (
                  <IdeaCard key={item.id} idea={item} showStatus />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      ) : (
        <>
          {drafts.length > 0 ? (
            <Card>
              <CardBody className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-fg">我的草稿</h2>
                  <span className="text-[11px] text-fg-subtle">只有你自己可见</span>
                </div>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {drafts.map((draft) => (
                    <IdeaCard key={draft.id} idea={draft} showStatus />
                  ))}
                </div>
              </CardBody>
            </Card>
          ) : null}

          <div className="kanban-scroll -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
            {COLUMNS.map((status) => {
              const bucket = grouped.get(status) ?? [];
              return (
                <section key={status} className="flex w-[19rem] shrink-0 flex-col gap-2">
                  <header className="flex items-center gap-2 px-1">
                    <h2 className="text-sm font-semibold text-fg">{IDEA_STATUS_LABELS[status]}</h2>
                    <Badge tone="neutral">{bucket.length}</Badge>
                    <span className="truncate text-[10px] text-fg-subtle" title={IDEA_STATUS_DESCRIPTIONS[status]}>
                      {IDEA_STATUS_DESCRIPTIONS[status]}
                    </span>
                  </header>
                  <div className="flex flex-col gap-2">
                    {bucket.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-[11px] text-fg-subtle">
                        暂无
                      </p>
                    ) : (
                      bucket.map((item) => <IdeaCard key={item.id} idea={item} />)
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      <p className="text-[11px] leading-relaxed text-fg-subtle">
        看板按状态分列，共 {IDEA_STATUSES.length} 种状态（含草稿与归档，后两者不常驻列）。
        卡片上的「未研判 / 研判中 / 已研判」反映最近一轮 AI 研判的状态。
      </p>
    </PageContainer>
  );
}
